import React from "react";
import { Check, Star } from "lucide-react";
import { cn } from "@/utils/utils";
import { useTranslations } from "next-intl";
import ButtonCheckout from "./ButtonCheckout";

interface PricingCardProps {
  plan: {
    name: string;
    description: string;
    price: number;
    priceAnchor?: number;
    features: { name: string }[];
    isFeatured?: boolean;
    priceId?: string;       // Stripe Price ID
    variantId?: string;     // LemonSqueezy variant ID (fallback)
    billingCycle?: 'monthly' | 'yearly' | 'one-time';
    mode?: 'subscription' | 'payment';
  };
  seriesId?: string;
}

export default function PricingCard({ plan, seriesId }: PricingCardProps) {
  const t = useTranslations('PricingCard');

  return (
    <div
      className={cn(
        "relative h-full rounded-3xl p-px",
        "overflow-hidden transition-all duration-500",
        plan.isFeatured
          ? "bg-gradient-to-b from-primary/30 via-primary/10 to-primary/5"
          : "bg-gradient-to-b from-gray-200/30 via-gray-200/10 to-transparent"
      )}
    >
      <div className={cn(
        "relative h-full rounded-[23px]",
        "backdrop-blur-xl transition-all duration-500 p-8",
        plan.isFeatured
          ? "bg-white/95 dark:bg-gray-900/95 border border-primary/20 shadow-lg shadow-primary/5"
          : "bg-white/90 dark:bg-gray-900/90 border border-gray-200/50"
      )}>
        {/* Popular badge */}
        {plan.isFeatured && (
          <div className="absolute -top-px left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-1.5 px-4 py-1 bg-gradient-to-r from-primary to-secondary rounded-b-lg shadow-md">
              <Star className="w-4 h-4 text-white" fill="white" />
              <span className="text-sm font-medium text-white">{t('mostPopular')}</span>
            </div>
          </div>
        )}

        {/* Plan name & description */}
        <div className="mb-8">
          <h3 className={cn(
            "text-2xl font-bold mb-3",
            plan.isFeatured
              ? "bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
              : "text-gray-900 dark:text-white"
          )}>
            {plan.name}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {plan.description}
          </p>
        </div>

        {/* Pricing */}
        <div className="mb-8">
          <div className="flex items-baseline gap-2">
            <span className={cn(
              "text-4xl font-bold",
              plan.isFeatured ? "text-primary" : "text-gray-900 dark:text-white"
            )}>
              ${plan.price}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {plan.billingCycle === 'yearly' ? t('perYear') : (plan.billingCycle === 'monthly' ? t('perMonth') : '')}
            </span>
          </div>
          {plan.billingCycle === 'yearly' && (
            <p className="mt-1 text-sm text-primary font-medium">
              {t('equivalentMonthly', { price: (plan.price / 12).toFixed(2) })}
            </p>
          )}
          {plan.priceAnchor && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="line-through">
                ${plan.priceAnchor}
              </span>
              <span className="ml-2 text-primary font-medium">
                {t('save')} ${(plan.priceAnchor - plan.price).toFixed(2)}
              </span>
            </p>
          )}
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8">
          {plan.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-start gap-3 text-gray-700 dark:text-gray-300"
            >
              <div className={cn(
                "rounded-full p-1 flex-shrink-0",
                plan.isFeatured
                  ? "bg-primary/15 text-primary"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
              )}>
                <Check className="w-4 h-4" />
              </div>
              <span className="text-sm leading-relaxed">{feature.name}</span>
            </div>
          ))}
        </div>

        {/* Action button */}
        <div className="mt-8">
          <ButtonCheckout
            priceId={plan.priceId}
            variantId={plan.variantId}
            seriesId={seriesId}
            mode={plan.mode}
          >
            {seriesId ? t('startSeries') : t('button')}
          </ButtonCheckout>
        </div>
      </div>
    </div>
  );
}